import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import WhatsAppButton from "@/components/WhatsAppButton";
import Breadcrumb from "@/components/Breadcrumb";
import ResponsiveHeroBanner from "@/components/ui/responsive-hero-banner";
import CookieConsent from "@/components/CookieConsent";

const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const ServiceDetailPage = lazy(() => import("@/pages/ServiceDetailPage"));
const BlogsPage = lazy(() => import("@/pages/BlogsPage"));
const BlogDetailPage = lazy(() => import("@/pages/BlogDetailPage"));
const ResourcesPage = lazy(() => import("@/pages/ResourcesPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@/pages/TermsOfServicePage"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/* ═══ SCROLL PROGRESS BAR ═══ */
function ScrollProgressBar() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });
  return (
    <motion.div
      className="scroll-progress-bar"
      style={{ scaleX }}
      aria-hidden="true"
    />
  );
}

/* ═══ ANIMATED PAGE WRAPPER ═══ */
function AnimatedPage({ children }) {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        exit={{ opacity: 0, y: -8, filter: "blur(4px)" }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

/* ═══ FLOATING AMBIENT PARTICLES ═══ */
function FloatingParticles() {
  return (
    <div className="fixed inset-0 pointer-events-none z-[2]" aria-hidden="true">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="float-dot"
          style={{
            left: `${15 + i * 18}%`,
            top: `${20 + (i % 3) * 25}%`,
          }}
        />
      ))}
    </div>
  );
}

function AppLayout() {
  return (
    <div className="min-h-screen bg-[#0A0F1C] text-[#FDFBF7]">
      {/* Skip-to-content for accessibility (WCAG 2.1 AA) */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-6 focus:py-3 focus:bg-[#D97706] focus:text-[#0A0F1C] focus:font-heading focus:font-bold focus:text-sm focus:rounded-full focus:shadow-lg focus:outline-none" data-testid="skip-to-content">
        Skip to main content
      </a>
      <div className="noise-overlay" aria-hidden="true" />
      <FloatingParticles />
      <ScrollProgressBar />
      <ScrollToTop />
      <Navbar />
      <main id="main-content" role="main">
        <Breadcrumb />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><motion.div className="w-8 h-8 border-2 border-[#D97706]/30 border-t-[#D97706] rounded-full" animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} /></div>}>
        <AnimatedPage>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:slug" element={<ServiceDetailPage />} />
          <Route path="/blogs" element={<BlogsPage />} />
          <Route path="/blogs/:slug" element={<BlogDetailPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </AnimatedPage>
        </Suspense>
      </main>
      <Footer />
      <WhatsAppButton />
      <CookieConsent />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Standalone fullscreen demo — no Navbar/Footer */}
        <Route path="/hero-demo" element={<ResponsiveHeroBanner />} />

        {/* All other routes go through the main AppLayout */}
        <Route path="*" element={<AppLayout />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
